import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, IconButton, Tooltip, Divider,
  Avatar, Checkbox, LinearProgress, Collapse, TablePagination,
} from '@mui/material';
import {
  Add, Refresh, AttachMoney, CheckCircle, Cancel, Calculate,
  ExpandMore, ExpandLess, Business, People, AccountBalance,
  CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import { useAuth } from '../../context/AuthContext';
import { getCachedDepartments } from '../../services/ApiService';
import dayjs from 'dayjs';

const STATUS_TABS = [
  { key: 'overview', label: 'Overview',  bg: '#e8eaf6', color: '#1a237e' },
  { key: 'Success',  label: 'Paid',      bg: '#e8f5e9', color: '#2e7d32' },
  { key: 'Pending',  label: 'Pending',   bg: '#fff3e0', color: '#e65100' },
  { key: 'Failed',   label: 'Failed',    bg: '#ffebee', color: '#c62828' },
];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const SummaryCard = ({ label, value, color, bg, icon: Icon }) => (
  <Card sx={{ flex: 1, minWidth: 150, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon sx={{ fontSize: 18, color }} />
        </Box>
        <Typography fontSize={11} color="text.secondary" fontWeight={600}>{label}</Typography>
      </Box>
      <Typography fontSize={20} fontWeight={900} color={color}>₹{fmt(value)}</Typography>
    </CardContent>
  </Card>
);

export default function Salary() {
  const { user } = useAuth();
  const isHR     = user?.role === 'HR';
  const hrDept   = user?.department || '';
  const hrdepts  = hrDept.split(',');

  const [activeTab, setActiveTab] = useState('overview');
  const [deptFilter, setDeptFilter] = useState(isHR ? hrdepts[0] : 'All');
  const [departments, setDepartments] = useState([]);
  const [overviewMonth, setOverviewMonth] = useState(dayjs().month() + 1);
  const [overviewYear, setOverviewYear]   = useState(dayjs().year());

  const [overview, setOverview]         = useState([]);  
  const [byDept, setByDept]             = useState([]);  
  const [summary, setSummary]           = useState(null);
  const [overviewLoading, setOvLoading] = useState(false);
  const [expandedDepts, setExpandedDepts] = useState({});

  const [records, setRecords]   = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [page, setPage]         = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [selected, setSelected]   = useState(new Set());
  const [approving, setApproving] = useState(false);

  const [addDialog, setAddDialog] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [employees, setEmployees] = useState([]);
  const [addForm, setAddForm]     = useState({
    stdId: '', recipt_no: '', salary: '', status: 'Success', createdBy: '1', recipt_name: '', feesRecipt: '',
  });

  const [calcDialog, setCalcDialog]   = useState(false);
  const [calcEmpId, setCalcEmpId]     = useState('');
  const [calcEmpName, setCalcEmpName] = useState('');
  const [calcMonth, setCalcMonth]     = useState(dayjs().month() + 1);
  const [calcYear, setCalcYear]       = useState(dayjs().year());
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcResult, setCalcResult]   = useState(null);
  const [savingSalary, setSavingSalary] = useState(false);

  useEffect(() => {
    getCachedDepartments(Config.main_url).then(d => setDepartments(d));
  }, []);

  const loadEmployees = useCallback(async () => {
    const deptData = await InvokeApi(Config.main_url + apiList.getDeptartments, { status: 'Active' });
    const depts    = deptData?.department || [];
    const results  = await Promise.all(
      depts.map(d => InvokeApi(Config.main_url + apiList.getEmployeesByDepartment, { department: d.department, status: 'Active' }))
    );
    let all = [];
    results.forEach(r => { if (r?.employee) all = [...all, ...r.employee]; });
    setEmployees(all);
  }, []);

  const loadOverview = useCallback(async () => {
    setOvLoading(true);
    setSelected(new Set());
    const data = await InvokeApi(Config.main_url + apiList.getEmployeeSalaryOverview, {
      month: overviewMonth,
      year:  overviewYear,
      department: deptFilter === 'All' ? null : deptFilter,
    });
    if (data?.code === '200') {
      setOverview(data.overview || []);
      setByDept(data.by_department || []);
      setSummary(data.summary || null);
    } else {
      setOverview([]); setByDept([]); setSummary(null);
    }
    setOvLoading(false);
  }, [overviewMonth, overviewYear, deptFilter]);

  const loadTransactions = useCallback(async () => {
    if (activeTab === 'overview') return;
    setTxLoading(true);
    setSelected(new Set());
    const data = await InvokeApi(Config.main_url + apiList.getFeesDate, { status: activeTab });
    let result = data?.fees || [];
    if (isHR && hrDept) {
      const empData = await InvokeApi(Config.main_url + apiList.getEmployeesByDepartment, { department: deptFilter, status: 'Active' });
      const deptIds = new Set((empData?.employee || []).map(e => String(e.ID)));
      result = result.filter(r => deptIds.has(String(r.std_id)));
    }
    setRecords(result);
    setPage(0);
    setTxLoading(false);
  }, [activeTab, isHR, hrDept, deptFilter]);

  useEffect(() => {
    if (activeTab === 'overview') loadOverview();
    else loadTransactions();
  }, [activeTab, overviewMonth, overviewYear, deptFilter]);

  useEffect(() => { loadEmployees(); }, []);

  const handleApprove = async (id) => {
    const data = await InvokeApi(Config.main_url + apiList.deleteFees, { id, status: 'Success' });
    if (data?.code === '200') {
      if (activeTab === 'overview') loadOverview(); else loadTransactions();
    } else alert(data?.message || 'Failed');
  };

  const handleReject = async (id) => {
    const data = await InvokeApi(Config.main_url + apiList.deleteFees, { id, status: 'Failed' });
    if (data?.code === '200') {
      if (activeTab === 'overview') loadOverview(); else loadTransactions();
    } else alert(data?.message || 'Failed');
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Approve ${selected.size} pending salary payment(s)?`)) return;
    setApproving(true);
    const data = await InvokeApi(Config.main_url + apiList.bulkApproveSalary, {
      ids: [...selected],
      approved_by: user?.adminId || '1',
    });
    setApproving(false);
    if (data?.code === '200') {
      setSelected(new Set());
      if (activeTab === 'overview') loadOverview(); else loadTransactions();
      alert(data.message);
    } else alert(data?.message || 'Bulk approve failed');
  };

  const handleApproveEmployee = async (pendingIds) => {
    if (!pendingIds?.length) return;
    setApproving(true);
    const data = await InvokeApi(Config.main_url + apiList.bulkApproveSalary, {
      ids: pendingIds.map(Number),
      approved_by: user?.adminId || '1',
    });
    setApproving(false);
    if (data?.code === '200') loadOverview();
    else alert(data?.message || 'Failed');
  };

  const handleCalculate = async () => {
    if (!calcEmpId) { alert('Select an employee'); return; }
    setCalcLoading(true); setCalcResult(null);
    const data = await InvokeApi(Config.main_url + apiList.calculateMonthlySalary, {
      employee_id: calcEmpId, month: calcMonth, year: calcYear,
    });
    setCalcLoading(false);
    if (data?.code === '200') setCalcResult(data);
    else alert(data?.message || 'Calculation failed — ensure salary structure is set for this employee');
  };

  const handleSaveCalculated = async () => {
    if (!calcResult) return;
    setSavingSalary(true);
    const receiptNo = `SAL-${calcResult.year}-${String(calcResult.month).padStart(2, '0')}-${calcEmpId}`;
    const data = await InvokeApi(Config.main_url + apiList.addFees, {
      stdId: calcEmpId,
      recipt_no: receiptNo,
      recipt_name: calcEmpName || `Salary ${calcResult.month_name} ${calcResult.year}`,
      salary: calcResult.net_salary,
      status: 'Pending',
      feesRecipt: '',
      createdBy: user?.adminId || '1',
    });
    setSavingSalary(false);
    if (data?.code === '200') {
      setCalcDialog(false); setCalcResult(null);
      loadOverview();
      alert(`₹${fmt(calcResult.net_salary)} saved as Pending for ${calcEmpName}`);
    } else alert(data?.message || 'Failed to save salary');
  };

  const handleSave = async () => {
    if (!addForm.stdId || !addForm.salary) { alert('Employee and amount are required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addFees, { ...addForm, createdBy: user?.adminId || '1' });
    setSaving(false);
    if (data?.code === '200') { setAddDialog(false); loadTransactions(); }
    else alert(data?.message || 'Failed');
  };

  const f = (k) => (e) => setAddForm(p => ({ ...p, [k]: e.target.value }));
  const txTotal = useMemo(() => records.reduce((s, r) => s + parseFloat(r.salary || 0), 0), [records]);

  const pendingRecords = useMemo(() => records.filter(r => r.status === 'Pending'), [records]);
  const allChecked     = pendingRecords.length > 0 && pendingRecords.every(r => selected.has(r.ID));
  const someChecked    = pendingRecords.some(r => selected.has(r.ID)) && !allChecked;

  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(pendingRecords.map(r => r.ID)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const allPendingIds = useMemo(() =>
    overview.flatMap(e => e.pending_ids?.map(Number) || []),
  [overview]);

  const toggleOverviewAll = () => {
    if (allPendingIds.every(id => selected.has(id))) setSelected(new Set());
    else setSelected(new Set(allPendingIds));
  };
  const toggleOverviewEmp = (ids) => {
    const next = new Set(selected);
    const numIds = ids.map(Number);
    if (numIds.every(id => next.has(id))) numIds.forEach(id => next.delete(id));
    else numIds.forEach(id => next.add(id));
    setSelected(next);
  };

  const currentTab = STATUS_TABS.find(t => t.key === activeTab);
  const paginatedTx = records.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Salary Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'overview'
              ? `${overview.length} employees · ${MONTHS[overviewMonth - 1]} ${overviewYear}`
              : `${records.length} records · ₹${fmt(txTotal)}`}
            {isHR && hrDept && <span style={{ marginLeft: 8, color: '#880e4f', fontWeight: 700 }}>· {hrDept}</span>}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {selected.size > 0 && (
            <Button variant="contained" startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              onClick={handleBulkApprove} disabled={approving}
              sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700 }}>
              Approve {selected.size} Selected
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => activeTab === 'overview' ? loadOverview() : loadTransactions()}
              sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<Calculate />}
            onClick={() => { setCalcResult(null); setCalcEmpId(''); setCalcEmpName(''); setCalcDialog(true); }}
            sx={{ borderRadius: 2, fontWeight: 700, color: '#1a237e', borderColor: '#1a237e' }}>
            Calculate
          </Button>
          {!isHR && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialog(true)}
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
              Add Salary
            </Button>
          )}
        </Box>
      </Box>

      {activeTab === 'overview' && summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard label="Total Payable"  value={summary.total_payable} color="#1a237e" bg="#e8eaf6" icon={AccountBalance} />
          <SummaryCard label="Total Paid"     value={summary.total_paid}    color="#2e7d32" bg="#e8f5e9" icon={CheckCircle} />
          <SummaryCard label="Pending"        value={summary.total_pending} color="#e65100" bg="#fff3e0" icon={AttachMoney} />
          <SummaryCard label="Balance Due"    value={summary.total_balance} color="#c62828" bg="#ffebee" icon={AccountBalance} />
        </Box>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <Box sx={{ display: 'flex', px: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <CardContent sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {STATUS_TABS.map(s => (
              <Chip key={s.key} label={s.label} onClick={() => { setActiveTab(s.key); setSelected(new Set()); }}
                sx={{
                  fontWeight: 700, cursor: 'pointer',
                  background: activeTab === s.key ? s.bg : '#f5f6fa',
                  color: activeTab === s.key ? s.color : 'text.secondary',
                  border: activeTab === s.key ? `1.5px solid ${s.color}` : '1.5px solid transparent',
                }} />
            ))}
          </CardContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pr: 2 }}>
            {activeTab === 'overview' && (
              <>
                <TextField select size="small" label="Month" value={overviewMonth}
                  onChange={e => setOverviewMonth(Number(e.target.value))} sx={{ minWidth: 120 }}>
                  {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Year" value={overviewYear}
                  onChange={e => setOverviewYear(Number(e.target.value))} sx={{ minWidth: 90 }}>
                  {[dayjs().year(), dayjs().year() - 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </>
            )}
            {isHR ? (
              <TextField select size="small" label="Department" value={deptFilter}
                onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
                {hrdepts.map((d, i) => <MenuItem key={i} value={d}>{d}</MenuItem>)}
              </TextField>
            ) : (
              <TextField select size="small" label="Department" value={deptFilter}
                onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value="All">All Departments</MenuItem>
                {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
              </TextField>
            )}
          </Box>
        </Box>
      </Card>

      {activeTab === 'overview' && (
        <>
          {overviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <>
              {byDept.length > 0 && (
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business sx={{ color: '#1a237e', fontSize: 18 }} />
                      <Typography fontWeight={700} color="#1a237e" fontSize={14}>By Department</Typography>
                    </Box>
                    <Divider />
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#f5f6fa' }}>
                            {['Department', 'Employees', 'Payable', 'Paid', 'Pending', 'Balance'].map(h => (
                              <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {byDept.map(d => {
                            const paidPct = d.payable > 0 ? Math.round((d.paid / d.payable) * 100) : 0;
                            return (
                              <TableRow key={d.department} hover
                                onClick={() => setExpandedDepts(p => ({ ...p, [d.department]: !p[d.department] }))}
                                sx={{ cursor: 'pointer' }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {expandedDepts[d.department] ? <ExpandLess fontSize="small" sx={{ color: '#1a237e' }} /> : <ExpandMore fontSize="small" sx={{ color: '#aaa' }} />}
                                    <Typography fontWeight={700} fontSize={13} color="#1a237e">{d.department}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip icon={<People sx={{ fontSize: 13 }} />} label={d.count} size="small"
                                    sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>₹{fmt(d.payable)}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 13, color: '#2e7d32' }}>
                                  ₹{fmt(d.paid)}
                                  <LinearProgress variant="determinate" value={paidPct}
                                    sx={{ mt: 0.5, height: 3, borderRadius: 2, background: '#e8f5e9', '& .MuiLinearProgress-bar': { background: '#2e7d32' } }} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 13, color: d.pending > 0 ? '#e65100' : 'text.secondary' }}>
                                  {d.pending > 0 ? `₹${fmt(d.pending)}` : '—'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 13, color: d.balance > 0 ? '#c62828' : '#2e7d32' }}>
                                  {d.balance > 0 ? `₹${fmt(d.balance)}` : '✓ Done'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {allPendingIds.length > 0 && (
                  <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #f0f0f0', background: selected.size > 0 ? '#fff8e1' : 'transparent' }}>
                    <Checkbox
                      checked={allPendingIds.every(id => selected.has(id))}
                      indeterminate={allPendingIds.some(id => selected.has(id)) && !allPendingIds.every(id => selected.has(id))}
                      onChange={toggleOverviewAll}
                      sx={{ color: '#e65100', '&.Mui-checked': { color: '#e65100' }, '&.MuiCheckbox-indeterminate': { color: '#e65100' } }}
                    />
                    <Typography fontSize={13} color="text.secondary">
                      {selected.size > 0
                        ? <span style={{ fontWeight: 700, color: '#e65100' }}>{selected.size} pending selected</span>
                        : `Select pending to bulk approve`}
                    </Typography>
                  </Box>
                )}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#f5f6fa' }}>
                        {allPendingIds.length > 0 && <TableCell padding="checkbox" />}
                        {['Employee', 'Net Salary', 'Paid', 'Pending', 'Balance', 'Attendance', 'Actions'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overview.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                            No employee data for {MONTHS[overviewMonth - 1]} {overviewYear}
                          </TableCell>
                        </TableRow>
                      ) : overview.map(emp => {
                        const hasPending  = emp.pending_ids?.length > 0;
                        const numPendIds  = (emp.pending_ids || []).map(Number);
                        const empSelected = hasPending && numPendIds.every(id => selected.has(id));
                        const empSome     = hasPending && numPendIds.some(id => selected.has(id)) && !empSelected;
                        const showRow     = !deptFilter || deptFilter === 'All' || emp.department === deptFilter || expandedDepts[emp.department];

                        const deptCollapsed = deptFilter === 'All' && byDept.length > 0 && !expandedDepts[emp.department];
                        if (deptCollapsed) return null;

                        return (
                          <TableRow key={emp.employee_id} hover sx={{ opacity: emp.has_structure ? 1 : 0.6 }}>
                            {allPendingIds.length > 0 && (
                              <TableCell padding="checkbox">
                                {hasPending && (
                                  <Checkbox checked={empSelected} indeterminate={empSome}
                                    onChange={() => toggleOverviewEmp(numPendIds)}
                                    sx={{ color: '#e65100', '&.Mui-checked': { color: '#e65100' } }} />
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar src={emp.profile_pic}
                                  sx={{ width: 34, height: 34, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                                  {emp.name?.[0]}
                                </Avatar>
                                <Box>
                                  <Typography fontWeight={700} fontSize={13}>{emp.name}</Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Chip label={`#${emp.employee_id}`} size="small"
                                      sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10, height: 18 }} />
                                    <Chip label={emp.department} size="small"
                                      sx={{ background: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, fontSize: 10, height: 18 }} />
                                  </Box>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {emp.has_structure ? (
                                <Typography fontWeight={800} fontSize={14} color="#1a237e">₹{fmt(emp.net_salary)}</Typography>
                              ) : (
                                <Chip label="No structure" size="small" sx={{ background: '#fff3e0', color: '#e65100', fontSize: 10 }} />
                              )}
                              {emp.base_salary > 0 && (
                                <Typography fontSize={10} color="text.secondary">Base: ₹{fmt(emp.base_salary)}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={700} fontSize={13} color={emp.paid > 0 ? '#2e7d32' : 'text.disabled'}>
                                {emp.paid > 0 ? `₹${fmt(emp.paid)}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {emp.pending > 0 ? (
                                <Box>
                                  <Typography fontWeight={700} fontSize={13} color="#e65100">₹{fmt(emp.pending)}</Typography>
                                  <Chip label={`${emp.pending_ids.length} txn`} size="small"
                                    sx={{ background: '#fff3e0', color: '#e65100', fontSize: 10, height: 18 }} />
                                </Box>
                              ) : <Typography fontSize={12} color="text.disabled">—</Typography>}
                            </TableCell>
                            {/* Balance */}
                            <TableCell>
                              {emp.balance > 0 ? (
                                <Chip label={`₹${fmt(emp.balance)}`} size="small"
                                  sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 11 }} />
                              ) : emp.net_salary > 0 ? (
                                <Chip label="✓ Done" size="small"
                                  sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 11 }} />
                              ) : <Typography fontSize={12} color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {emp.attendance.present > 0 && (
                                  <Chip label={`P:${emp.attendance.present}`} size="small"
                                    sx={{ background: '#e8f5e9', color: '#2e7d32', fontSize: 10, height: 18 }} />
                                )}
                                {emp.attendance.absent > 0 && (
                                  <Chip label={`A:${emp.attendance.absent}`} size="small"
                                    sx={{ background: '#ffebee', color: '#c62828', fontSize: 10, height: 18 }} />
                                )}
                                {emp.attendance.half_day > 0 && (
                                  <Chip label={`H:${emp.attendance.half_day}`} size="small"
                                    sx={{ background: '#fff3e0', color: '#e65100', fontSize: 10, height: 18 }} />
                                )}
                                {emp.attendance.leave > 0 && (
                                  <Chip label={`L:${emp.attendance.leave}`} size="small"
                                    sx={{ background: '#e3f2fd', color: '#1565c0', fontSize: 10, height: 18 }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {hasPending && (
                                  <Button size="small" variant="contained" startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                                    onClick={() => handleApproveEmployee(numPendIds)}
                                    disabled={approving}
                                    sx={{ background: '#2e7d32', borderRadius: 1.5, fontSize: 11, fontWeight: 700, px: 1.2 }}>
                                    Approve
                                  </Button>
                                )}
                                {emp.balance > 0 && emp.has_structure && (
                                  <Button size="small" variant="outlined" startIcon={<Calculate sx={{ fontSize: 13 }} />}
                                    onClick={() => {
                                      setCalcEmpId(String(emp.employee_id));
                                      setCalcEmpName(emp.name);
                                      setCalcMonth(overviewMonth);
                                      setCalcYear(overviewYear);
                                      setCalcResult(null);
                                      setCalcDialog(true);
                                    }}
                                    sx={{ borderRadius: 1.5, fontSize: 11, fontWeight: 700, px: 1.2, color: '#1a237e', borderColor: '#1a237e' }}>
                                    Pay
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          )}
        </>
      )}

      {activeTab !== 'overview' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {activeTab === 'Pending' && pendingRecords.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #f0f0f0', background: selected.size > 0 ? '#fff8e1' : '#fafafa' }}>
              <Checkbox
                checked={allChecked} indeterminate={someChecked} onChange={toggleAll}
                sx={{ color: '#e65100', '&.Mui-checked': { color: '#e65100' }, '&.MuiCheckbox-indeterminate': { color: '#e65100' } }}
              />
              <Typography fontSize={13} color="text.secondary">
                {selected.size > 0
                  ? <span style={{ fontWeight: 700, color: '#e65100' }}>{selected.size} of {pendingRecords.length} selected</span>
                  : 'Select all pending to bulk approve'}
              </Typography>
            </Box>
          )}

          {txLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      {activeTab === 'Pending' && <TableCell padding="checkbox" />}
                      {['#', 'Receipt No', 'Emp ID', 'Name', 'Amount', 'Status', 'Date',
                        ...(activeTab === 'Pending' ? ['Actions'] : [])].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTx.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No {activeTab.toLowerCase()} salary records
                        </TableCell>
                      </TableRow>
                    ) : paginatedTx.map((r, i) => {
                      const tab = STATUS_TABS.find(s => s.key === r.status);
                      return (
                        <TableRow key={r.ID} hover sx={{ background: selected.has(r.ID) ? '#fff8e1' : 'transparent' }}>
                          {activeTab === 'Pending' && (
                            <TableCell padding="checkbox">
                              <Checkbox checked={selected.has(r.ID)} onChange={() => toggleOne(r.ID)}
                                sx={{ color: '#e65100', '&.Mui-checked': { color: '#e65100' } }} />
                            </TableCell>
                          )}
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{page * rowsPerPage + i + 1}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.recipt_no}</TableCell>
                          <TableCell>
                            <Chip label={`#${r.std_id}`} size="small"
                              sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10 }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.recipt_name || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 800, color: '#2e7d32' }}>
                            ₹{fmt(r.salary)}
                          </TableCell>
                          <TableCell>
                            <Chip label={r.status} size="small"
                              sx={{ background: tab?.bg || '#f5f5f5', color: tab?.color || '#333', fontWeight: 600, fontSize: 10 }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {r.created_at ? dayjs(r.created_at).format('DD MMM YYYY') : '—'}
                          </TableCell>
                          {activeTab === 'Pending' && (
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button size="small" variant="contained" startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                                  onClick={() => handleApprove(r.ID)}
                                  sx={{ background: '#2e7d32', borderRadius: 1.5, fontSize: 11, fontWeight: 700, px: 1.5 }}>
                                  Approve
                                </Button>
                                <Button size="small" variant="contained" startIcon={<Cancel sx={{ fontSize: 13 }} />}
                                  onClick={() => handleReject(r.ID)}
                                  sx={{ background: '#c62828', borderRadius: 1.5, fontSize: 11, fontWeight: 700, px: 1.5 }}>
                                  Reject
                                </Button>
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={records.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
              />
            </>
          )}
        </Card>
      )}

      <Dialog open={calcDialog} onClose={() => setCalcDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Monthly Salary Calculator
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Select Employee *" value={calcEmpId}
                onChange={e => {
                  setCalcEmpId(e.target.value);
                  const emp = employees.find(x => String(x.ID) === String(e.target.value));
                  setCalcEmpName(emp?.name || '');
                  setCalcResult(null);
                }}>
                <MenuItem value="">— Select Employee —</MenuItem>
                {employees.map(e => (
                  <MenuItem key={e.ID} value={String(e.ID)}>#{e.ID} — {e.name} ({e.department})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Month" value={calcMonth}
                onChange={e => { setCalcMonth(e.target.value); setCalcResult(null); }}>
                {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Year" value={calcYear}
                onChange={e => { setCalcYear(e.target.value); setCalcResult(null); }}>
                {[dayjs().year(), dayjs().year() - 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth variant="contained" onClick={handleCalculate}
                disabled={calcLoading || !calcEmpId}
                startIcon={calcLoading ? <CircularProgress size={16} color="inherit" /> : <Calculate />}
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                {calcLoading ? 'Calculating...' : 'Calculate Salary'}
              </Button>
            </Grid>
          </Grid>

          {calcResult && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ p: 2.5, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', mb: 2 }}>
                <Typography fontSize={12} sx={{ opacity: 0.8 }}>{calcEmpName} · {calcResult.month_name} {calcResult.year}</Typography>
                <Typography fontSize={28} fontWeight={900}>₹{fmt(calcResult.net_salary)}</Typography>
                <Typography fontSize={11} sx={{ opacity: 0.7 }}>Net Salary</Typography>
              </Box>
              <Grid container spacing={1}>
                {[
                  { label: 'Base Salary',         value: `₹${fmt(calcResult.base_salary)}`,                                                                         color: '#1a237e' },
                  { label: 'Days in Month',        value: calcResult.days_in_month,                                                                                  color: '#555' },
                  { label: 'Daily Rate',           value: `₹${parseFloat(calcResult.daily_rate).toFixed(2)}`,                                                        color: '#555' },
                  { label: 'Hourly Rate',          value: `₹${parseFloat(calcResult.hourly_rate).toFixed(2)}`,                                                       color: '#555' },
                  { label: 'Present Days',         value: calcResult.attendance?.present_days,                                                                       color: '#2e7d32' },
                  { label: 'Absent Days',          value: calcResult.attendance?.absent_days,                                                                        color: '#c62828' },
                  { label: 'Half Day',             value: `${calcResult.attendance?.half_day ?? 0}  −₹${parseFloat(calcResult.deductions?.half_day_deduction ?? 0).toFixed(2)}`, color: '#e65100' },
                  { label: 'Leave Days',           value: calcResult.attendance?.leave_days,                                                                         color: '#1565c0' },
                  { label: 'Week Off',             value: calcResult.attendance?.week_off_days,                                                                      color: '#00695c' },
                  { label: 'Absent Deduction',     value: `−₹${parseFloat(calcResult.deductions?.absent_deduction_amount ?? 0).toFixed(2)}`,                        color: '#c62828' },
                  { label: 'Time Deduction',       value: `${calcResult.deductions?.deduction_hours ?? 0}h  −₹${parseFloat(calcResult.deductions?.time_deduction_amount ?? 0).toFixed(2)}`, color: '#c62828' },
                  { label: 'Fines',                value: `−₹${parseFloat(calcResult.deductions?.fines_amount ?? 0).toFixed(2)}`,                                   color: '#c62828' },
                ].map(({ label, value, color }) => (
                  <Grid item xs={6} sm={4} key={label}>
                    <Box sx={{ p: 1.2, borderRadius: 2, border: '1px solid #f0f0f0', background: '#fafafa' }}>
                      <Typography fontSize={10} color="text.secondary">{label}</Typography>
                      <Typography fontSize={13} fontWeight={700} color={color}>{value ?? '—'}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setCalcDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
          {calcResult && (
            <Button onClick={handleSaveCalculated} variant="contained" disabled={savingSalary}
              startIcon={savingSalary ? <CircularProgress size={16} color="inherit" /> : <AttachMoney />}
              sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700 }}>
              {savingSalary ? 'Saving...' : `Save ₹${fmt(calcResult.net_salary)} as Pending`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add Salary Record
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Employee *" value={addForm.stdId}
                onChange={e => {
                  const emp = employees.find(x => String(x.ID) === String(e.target.value));
                  setAddForm(p => ({ ...p, stdId: e.target.value, recipt_name: emp?.name || '' }));
                }}>
                <MenuItem value="">— Select Employee —</MenuItem>
                {employees.map(e => (
                  <MenuItem key={e.ID} value={String(e.ID)}>#{e.ID} — {e.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Receipt No" value={addForm.recipt_no} onChange={f('recipt_no')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Name / Note" value={addForm.recipt_name} onChange={f('recipt_name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Amount *" type="number" value={addForm.salary} onChange={f('salary')}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Status" value={addForm.status} onChange={f('status')}>
                {STATUS_TABS.filter(s => s.key !== 'overview').map(s => (
                  <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setAddDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
